import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getCurrencyCode, getLanguageCode} from 'modules/meta/selectors'

import {Button} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import mirror from '@vio/atlas/src/utils/mirror'
import {cssTheme} from '@vio/atlas/themes'
import {getAnonymousId} from '@vio/core/src/_web/anonymousId'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getBookingManagementUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getApplication} from '@vio/shared/src/dealFreeze/modules/selector'
import {getDealFreezePaymentUrl} from '@vio/shared/src/dealFreeze/utils/url'

interface Props {
  bookingId: string
  isCompactLayout?: boolean
}

const ChevronWrapper = styled.div`
  ${mirror()}
`

const Wrapper = styled.div`
  display: flex;
  justify-between: space-between;
  align-items: center;
  flex-direction: column-reverse;
  gap: ${cssTheme.layout.spacing.s300};
  .--complete-booking-cta,
  .--manage-freeze-cta {
    width: 100%;
  }

  &.--compact {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;

    .--complete-booking-cta,
    .--manage-freeze-cta {
      flex-grow: 1;
      width: auto;
    }
  }
`

export const FrozenRoomOfferCTA: React.FC<Props> = ({
  bookingId,
  isCompactLayout = false
}) => {
  const {checkoutUrl} = useSelector(getApplication)
  const {brand} = useBrand()
  const locale = useSelector(getLanguageCode)
  const currency = useSelector(getCurrencyCode)

  const anonymousId = getAnonymousId() as string
  const bookingUrl = getBookingManagementUrl({
    brand,
    appEnv: process.env.NEXT_PUBLIC_ENV || '',
    bookingId,
    anonymousId,
    locale,
    currency
  })
  const paymentUrl = getDealFreezePaymentUrl({
    hostUrl: checkoutUrl,
    bookingId,
    locale
  })

  const handleManageBooking = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (bookingId) window.location.href = bookingUrl
  }

  const handleCompleteBooking = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (checkoutUrl && bookingId) window.location.href = paymentUrl
  }
  return (
    <Wrapper className={cx(isCompactLayout && '--compact')}>
      <div className="--manage-freeze-cta">
        <Button
          size="lg"
          variant="secondary"
          fullWidth
          onClick={handleManageBooking}
        >
          <FormattedMessageWrapper id="viewDeal" defaultMessage="View deal" />
        </Button>
      </div>
      <div className="--complete-booking-cta">
        <Button
          size="lg"
          variant="primary"
          fullWidth
          iconEnd={
            <ChevronWrapper>
              <Icon name="ChevronRight" />
            </ChevronWrapper>
          }
          onClick={handleCompleteBooking}
        >
          <FormattedMessageWrapper
            id="completeBooking"
            defaultMessage="Complete booking"
          />
        </Button>
      </div>
    </Wrapper>
  )
}
